import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "../lib/api";
import { ArrowLeft } from "lucide-react";
import { track } from "../lib/analytics";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
};

export const BlogPost = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    http
      .get(`/blog/articles/${slug}`)
      .then((r) => setArticle(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "not found" : e?.message || "failed to load"))
      .finally(() => setLoading(false));
  }, [slug]);

  // Update document title + meta description for SEO once loaded
  useEffect(() => {
    if (article?.title) {
      document.title = `${article.title} — sAimonSays.Ai`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta && article.meta_description) meta.setAttribute("content", article.meta_description);
      track("blog_article_viewed", { slug: article.slug, title: article.title });
    }
    return () => { document.title = "sAimonSays.Ai"; };
  }, [article]);

  return (
    <div data-testid="blog-post-page" className="mx-auto max-w-3xl px-6 py-16">
      <Link
        to="/blog"
        data-testid="blog-back"
        className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-400 hover:text-[color:var(--accent-amber)] mb-8 duration-150"
        style={{ transitionProperty: "color" }}
      >
        <ArrowLeft className="w-3 h-3" /> back to field notes
      </Link>

      {loading && <div className="text-neutral-500 text-sm" data-testid="post-loading">loading...</div>}
      {error && (
        <div data-testid="post-error" className="border border-white/10 bg-[#0a0a0a] p-8 text-center">
          <div className="text-neutral-400 mb-2">{error}</div>
          <Link to="/blog" className="text-[color:var(--accent-amber)] text-xs uppercase tracking-widest">
            &larr; back to blog
          </Link>
        </div>
      )}

      {article && (
        <article data-testid="post-article">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)] mb-3">
            // {fmtDate(article.published_at)}
          </div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-6">
            {article.title}
          </h1>
          {article.meta_description && (
            <p className="text-lg text-neutral-400 mb-8 leading-relaxed">{article.meta_description}</p>
          )}
          {article.hero_image_url && (
            <img
              src={article.hero_image_url}
              alt={article.title}
              className="w-full border border-white/10 mb-10"
            />
          )}
          <div
            data-testid="post-body"
            className="blog-prose"
            dangerouslySetInnerHTML={{ __html: article.content_html || "" }}
          />
          {Array.isArray(article.keywords) && article.keywords.length > 0 && (
            <div className="mt-12 pt-8 border-t border-white/10">
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">tags</div>
              <div className="flex flex-wrap gap-1.5">
                {article.keywords.slice(0, 12).map((k) => (
                  <span key={k} className="text-[10px] uppercase tracking-widest border border-white/10 px-2 py-1 text-neutral-400">
                    {k}
                  </span>
                ))}
              </div>
            </div>
          )}
        </article>
      )}
    </div>
  );
};
